import { useState } from "react";
import {
  Alert,
  Button,
  ButtonBase,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { useMe } from "../api";
import {
  useCancelAvailability,
  useEnableAvailability,
  useRequestAvailability,
} from "../api";

const ShiftDayType = ({
  cases = [],
  shiftGroups = {},
  scheduleType,
  isOpen,
  setIsOpen,
  selectedScheduleType,
  setSelectedScheduleType,
  date,
  showUpcoming,
}) => {
  const {
    mutateAsync: cancelAvailability,
    isLoading: cancelAvailabilityLoading,
  } = useCancelAvailability();
  const {
    mutateAsync: enableAvailability,
    isLoading: enableAvailabilityLoading,
  } = useEnableAvailability();
  const {
    mutateAsync: requestAvailability,
    isLoading: requestAvailabilityLoading,
  } = useRequestAvailability();
  const { data: me } = useMe();

  const isLoading =
    cancelAvailabilityLoading ||
    enableAvailabilityLoading ||
    requestAvailabilityLoading;
  const { unassigned = [], scheduled = [] } = shiftGroups;
  const isScheduled = scheduled.length > 0;
  const isAutoassignable = unassigned
    .flatMap(({ shifts }) => shifts)
    .some(({ isAutoassignable }) => isAutoassignable);

  const isRequested = cases.some(({ status }) => status !== "Cancelled");
  const hasRequested = cases.some(({ status }) => status === "Cancelled");

  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const isActive = isOpen && selectedScheduleType === scheduleType;

  if (isScheduled) {
    const [shift] = scheduled;
    const { endTime, startTime } = shift;

    return (
      <div>
        <div className="relative">
          <Button
            disableRipple
            disableTouchRipple
            variant="contained"
            disabled
            className="!normal-case !px-[10px] [&.Mui-disabled]:!bg-[var(--color-blue)] [&.Mui-disabled]:!text-[var(--color-white)]"
          >
            scheduled
          </Button>
          <Typography className="absolute w-full text-center">
            {startTime.replaceAll(":", "")} - {endTime.replaceAll(":", "")}
          </Typography>
        </div>
      </div>
    );
  }

  const renderSwitch = (
    <div>
      <ButtonBase
        className="hover:!bg-transparent"
        disabled={isLoading}
        disableRipple
        disableTouchRipple
        onClick={async () => {
          try {
            if (isRequested) {
              const { id: caseId } = cases.find(
                ({ status }) => status !== "Cancelled",
              );
              await cancelAvailability(caseId);
              return;
            }
            if (hasRequested) {
              const { id: caseId } = cases.find(
                ({ status }) => status === "Cancelled",
              );
              await enableAvailability(caseId);
              setSuccessAlertOpen(true);
              return;
            }

            await requestAvailability({
              availabilityDate: date,
              shiftType: scheduleType,
            });
            setSuccessAlertOpen(true);
          } catch (error) {
            setErrorAlertOpen(true);
          }
        }}
      >
        <div className="relative">
          <div className="upcoming-shifts-toggle flex flex-col items-center justify-center gap-1">
            <span className="text-[13px] font-semibold text-[var(--color-blue)]">
              {isRequested ? "Available" : "Not Available"}
            </span>

            <Switch
              color="default"
              disabled={isLoading}
              checked={isRequested}
            />
          </div>
        </div>
      </ButtonBase>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={successAlertOpen}
        onClose={() => setSuccessAlertOpen(false)}
      >
        <Alert variant="filled" severity="success">
          You're Available!
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlertOpen}
        onClose={() => setErrorAlertOpen(false)}
      >
        <Alert variant="filled" severity="error">
          Error Requesting Availability
        </Alert>
      </Snackbar>
    </div>
  );

  if (isAutoassignable && me?.canAutoSchedule) {
    return (
      <div className="flex h-full w-full">
        <ButtonBase
          disableRipple
          onClick={() => {
            if (isActive) {
              setIsOpen(false);
            } else {
              setIsOpen(true);
              setSelectedScheduleType(scheduleType);
            }
          }}
          className="flex h-full w-full"
        >
          <div
            className={`flex h-full w-full items-center justify-center gap-1 ${
              isActive ? "bg-[var(--color-green-soft)]" : ""
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-green)] text-white text-[12px] font-bold">
              +
            </span>

            <span className="text-[12px] font-semibold text-[var(--color-green)]">
              Add Shift
            </span>
          </div>
        </ButtonBase>
      </div>
    );
  }

  return renderSwitch;
};

export { ShiftDayType };
